import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Check, Sparkles, TrendingUp, Users, Award, Zap, Shield, Clock } from 'lucide-react';
import { services, testimonials, companyInfo, blogPosts } from '../mock';
import PricingTabs from '../components/PricingTabs';
import SEO from '../components/SEO';
import * as Icons from 'lucide-react';

const Home = ({ onCtaClick }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-white text-gray-900">
      <SEO 
        title="IlinkTrading - Transform Ideas Into Digital Excellence"
        description="Leading digital agency specializing in web development, blockchain solutions, mobile apps, and digital marketing. Transform your business with cutting-edge technology."
        keywords="digital agency, web development, blockchain development, mobile app development, UI/UX design, digital marketing, custom software"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" />
        <div className="absolute top-40 left-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000" />
        
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
                <Sparkles className="text-blue-600" size={18} />
                <span className="text-sm font-medium text-gray-700">Welcome to IlinkTrading</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Empowering Ideas,
                </span>
                <br />
                <span className="text-gray-900">Elevating Success</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Transform your vision into reality with cutting-edge technology solutions. 
                We deliver innovation that drives growth and creates exceptional digital experiences.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => onCtaClick && onCtaClick()}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  Get Started
                  <ArrowRight size={20} />
                </button>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl text-base font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 border-2 border-gray-200"
                >
                  View Services
                </Link>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div>
                  <div className="text-3xl font-bold text-blue-600">{companyInfo.projects}</div>
                  <div className="text-sm text-gray-600">Projects</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">{companyInfo.clients}</div>
                  <div className="text-sm text-gray-600">Clients</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-pink-600">{companyInfo.teamSize}</div>
                  <div className="text-sm text-gray-600">Experts</div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-3xl transform rotate-6" />
              <img
                src="https://images.unsplash.com/photo-1644088379091-d574269d422f?w=800&q=85"
                alt="Digital Innovation"
                className="relative rounded-3xl shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
              <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider">Our Expertise</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Transform Your Business with
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Smart Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital services designed to elevate your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => {
              const IconComponent = Icons[service.icon];
              const colors = [
                { bg: 'from-blue-500 to-cyan-500', icon: 'text-blue-600', border: 'border-blue-200' },
                { bg: 'from-purple-500 to-pink-500', icon: 'text-purple-600', border: 'border-purple-200' },
                { bg: 'from-orange-500 to-red-500', icon: 'text-orange-600', border: 'border-orange-200' },
                { bg: 'from-green-500 to-emerald-500', icon: 'text-green-600', border: 'border-green-200' },
                { bg: 'from-indigo-500 to-purple-500', icon: 'text-indigo-600', border: 'border-indigo-200' },
                { bg: 'from-pink-500 to-rose-500', icon: 'text-pink-600', border: 'border-pink-200' },
              ];
              const color = colors[index % colors.length];
              
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 ${color.border} hover:-translate-y-2 block`}
                >
                  <div className={`h-2 bg-gradient-to-r ${color.bg}`} />
                  <div className="p-8">
                    <div className={`w-16 h-16 bg-gradient-to-br ${color.bg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      {IconComponent && <IconComponent className="text-white" size={32} strokeWidth={2} />}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                    <div className="flex items-center gap-2 text-blue-600 font-semibold group-hover:gap-3 transition-all">
                      Learn More <ArrowRight size={18} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => onCtaClick && onCtaClick()}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              View All Services
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl opacity-20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=85"
                alt="Our Team"
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
                <Users className="text-purple-600" size={18} />
                <span className="text-sm font-semibold text-gray-700">Who We Are</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Advancing Innovation
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Through Technology</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We specialize in IT consulting and software development, empowering businesses to harness 
                technology for sustainable growth. Our expertise spans industries, enabling companies to build 
                innovative, scalable, and high-performing digital solutions.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="text-blue-600" size={20} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Custom Application Development</h4>
                    <p className="text-sm text-gray-600">Tailored solutions for your unique needs</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="text-purple-600" size={20} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Strategic Marketing Solutions</h4>
                    <p className="text-sm text-gray-600">Data-driven strategies for growth</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onCtaClick && onCtaClick()}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Get a Free Quote
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full">
              <Award className="text-purple-600" size={18} />
              <span className="text-sm font-semibold text-purple-600 uppercase tracking-wider">Why Partner With Us</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Trusted Technology Partner
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Team</h3>
              <p className="text-gray-600">{companyInfo.teamSize} experienced professionals dedicated to your success</p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Proven Results</h3>
              <p className="text-gray-600">{companyInfo.projects} projects delivered with excellence</p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Growth Focused</h3>
              <p className="text-gray-600">Strategies that deliver measurable business results</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl opacity-20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=85"
                alt="Our Values"
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <Shield className="text-blue-600" size={18} />
                <span className="text-sm font-semibold text-blue-600">Our Core Values</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Built on Trust,
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Driven by Excellence</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We believe in building long-term partnerships based on transparency, innovation, and measurable results. Our commitment to excellence drives everything we do.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Innovation First</h4>
                    <p className="text-sm text-gray-600">Cutting-edge solutions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Timely Delivery</h4>
                    <p className="text-sm text-gray-600">On-time, every time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full">
              <TrendingUp className="text-green-600" size={18} />
              <span className="text-sm font-semibold text-green-600 uppercase tracking-wider">Transparent Pricing</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Choose Your Perfect
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Plan</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible pricing options designed to match your needs and budget. Get started with our combo packages or choose individual services.
            </p>
          </div>

          <PricingTabs showAllServices={true} onCtaClick={onCtaClick} />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
              <Star className="text-yellow-500" size={18} fill="currentColor" />
              <span className="text-sm font-semibold text-gray-700">Client Success Stories</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Loved by Industry Leaders
            </h2>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-12 relative">
            <div className="absolute top-8 left-8 text-blue-200 opacity-50">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </div>
            <div className="flex gap-1 justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} className="text-yellow-500" fill="currentColor" />
              ))}
            </div>
            <p className="text-xl md:text-2xl text-gray-700 text-center mb-8 italic leading-relaxed">
              "{testimonials[currentTestimonial].content}"
            </p>
            <div className="flex flex-col items-center">
              <img
                src={testimonials[currentTestimonial].image}
                alt={testimonials[currentTestimonial].name}
                className="w-16 h-16 rounded-full object-cover mb-4 border-4 border-blue-100"
              />
              <h4 className="text-lg font-bold text-gray-900">{testimonials[currentTestimonial].name}</h4>
              <p className="text-gray-600">{testimonials[currentTestimonial].role}</p>
            </div>

            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentTestimonial 
                      ? 'w-8 h-3 bg-gradient-to-r from-blue-600 to-purple-600' 
                      : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
              <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider">Latest Insights</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Expert Insights & Trends
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article
                key={post.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:-translate-y-2"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute top-4 right-4 px-3 py-1 bg-white rounded-full text-xs font-semibold text-blue-600 shadow-md">
                    {post.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{post.readTime}</span>
                    <button className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all">
                      Read More <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-white/90">
            Let's build something exceptional together. Get in touch with our team today.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => onCtaClick && onCtaClick()}
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl text-base font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Start Your Project
              <ArrowRight size={20} />
            </button>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl text-base font-semibold border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
            >
              View Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
